import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { ApiService } from 'app/shared/api.service';
import { environment } from 'environments/environment';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private api: ApiService,
        private key: StorageService,
        private router: Router
    ) {}

    get accessToken(): string {
        const encryptedToken = localStorage.getItem('a_id');
        if (encryptedToken) {
            try {
                return JSON.parse(this.key.decryptionAES(encryptedToken));
            } catch (e) {
                console.error('Failed to decrypt token:', e);
                return '';
            }
        }
        return '';
    }

    /**
     * Sign in
     */
    async signIn(credentials: {
        email: string;
        password: string;
    }): Promise<any> {
        if (this._authenticated) {
            await this.signOut().toPromise();
        }

        const payload = {
            email: credentials.email,
            password: credentials.password,
        };

        try {
            const response: any = await this.api.post(
                environment.auth,
                payload,
                false
            );
            console.log(this.api);
            console.log('Login Response:', response);

            if (response && response.success) {
                const userFromApi = response.data.user;
                const IsTenant = response.is_tenant;

                if (!userFromApi) {
                    throw new Error('Invalid response from server');
                }

                const token = response.data.access_token;
                const expiresIn = response.data.expires_in || 3600;
                const expiredAt = Date.now() + expiresIn * 1000;

                // Store encrypted token
                localStorage.setItem('a_exp', this.key.encryptData(expiredAt));
                localStorage.setItem('a_id', this.key.encryptData(token));

                // Extract permissions
                const permissions =
                    userFromApi.roles?.flatMap(
                        (role: any) => role.permissions || []
                    ) || [];

                const hasTenantLoket = permissions.some((p: string) =>
                    p.toLowerCase().includes('tenant.loket')
                );

                const hasTenantAdmin = permissions.some((p: string) =>
                    p.toLowerCase().includes('tenant.admin')
                );

                const services = userFromApi.services || [];

                const userData = {
                    id: userFromApi.id || '',
                    name: userFromApi.name || 'User',
                    username: userFromApi.username || userFromApi.email || '',
                    email: userFromApi.email || '',
                    role: userFromApi.role || 'user',
                    avatar: userFromApi.avatar || '',
                    status: 'login',
                    is_tenant: IsTenant,
                    permissions: permissions,
                    gates: userFromApi.gates || [],
                    services: services, // ✅ Store services

                    // Untuk roleguard
                    has_parking_access: hasTenantLoket,
                    is_tenant_admin: hasTenantAdmin,

                    // We will set service_type later, or default to first code if available
                    service_type: services.length > 0 ? services[0].code : (response.service_type || 'parking')
                };

                // Save encrypted user
                localStorage.setItem('user', this.key.encryptData(userData));
                this._userService.user = userData;
                this._authenticated = true;

                // Start refresh token scheduler
                if (this.api.startTokenScheduler) {
                    this.api.startTokenScheduler(expiresIn);
                }

                return { success: true, services: services };
            }

            return false;
        } catch (error: any) {
            console.error('Login failed:', error);

            let errorMessage = 'Login failed. Please check your credentials.';

            if (error?.message) {
                errorMessage = error.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            throw new Error(errorMessage);
        }
    }

    /**
     * Sign in using token (auto login)
     */
    signInUsingToken(): Observable<boolean> {
        const tokenEnc = localStorage.getItem('a_id');
        const userEnc = localStorage.getItem('user'); // GANTI KEY JADI 'user'

        if (tokenEnc && userEnc) {
            try {
                const token = this.key.decryptionAES(tokenEnc);
                const user = JSON.parse(this.key.decryptionAES(userEnc));

                // console.log('Auto login with user:', user);

                // Pastikan user punya role
                if (!user.role) {
                    user.role = 'user';
                }

                this._userService.user = user;
                this._authenticated = true;

                return of(true);
            } catch (e) {
                console.error('Token decryption failed:', e);
                this.signOut();
                return of(false);
            }
        }
        return of(false);
    }

    /**
     * Sign out - HIT API LOGOUT
     */
    signOut(): Observable<boolean> {
        return new Observable((observer) => {
            if (this.accessToken) {
                this.api
                    .post('api/v1/auth/logout', {}, true)
                    .then((response: any) => {
                        console.log('Logout API Response:', response);
                        this.clearAuthData();
                        observer.next(true);
                        observer.complete();
                    })
                    .catch((error) => {
                        console.error('Logout API Error:', error);
                        this.clearAuthData();
                        observer.next(true);
                        observer.complete();
                    });
            } else {
                this.clearAuthData();
                observer.next(true);
                observer.complete();
            }
        });
    }

    /**
     * Clear authentication data
     */
    private clearAuthData(): void {
        localStorage.clear();
        this._authenticated = false;
        this._userService.user = null;
    }

    /**
     * Check authentication status
     */
    check(): Observable<boolean> {
        if (this._authenticated) {
            return of(true);
        }

        if (!this.accessToken) {
            return of(false);
        }

        return this.signInUsingToken();
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this._authenticated;
    }

    /**
     * Get current logged in user
     */
    getCurrentUser(): any {
        const userEnc = localStorage.getItem('user'); // KEY HARUS 'user'
        if (!userEnc) {
            console.log('No user data in localStorage');
            return null;
        }

        try {
            const user = JSON.parse(this.key.decryptionAES(userEnc));
            return user;
        } catch (error) {
            console.error('Error decrypting user data:', error);
            return null;
        }
    }

    /**
     * Get user name
     */
    getUserName(): string {
        const user = this.getCurrentUser();
        return user?.name || 'Guest';
    }

    /**
     * Get specific user property
     */
    getUserProperty(property: string): any {
        const user = this.getCurrentUser();
        return user ? user[property] : null;
    }

    /**
     * Check if user is logged in
     */
    isLoggedIn(): boolean {
        return !!this.getCurrentUser();
    }

    /**
     * Get user gates
     */
    getUserGates(): any[] {
        const user = this.getCurrentUser();
        return user?.gates || [];
    }

    /**
     * Check if user has specific gate
     */
    hasGate(gateId: string): boolean {
        const gates = this.getUserGates();
        return gates.some((gate) => gate.id === gateId);
    }
    /**
     * Update active service type
     */
    updateServiceType(code: string): void {
        console.log('AuthService: updateServiceType called with', code);
        const user = this.getCurrentUser();
        if (user) {
            user.service_type = code;
            localStorage.setItem('user', this.key.encryptData(user));
            this._userService.user = user;
            console.log('AuthService: User Updated', user);
        } else {
            console.error('AuthService: No user found to update');
        }
    }
}
